import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
	Table,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	CircularProgress,
} from "@mui/material";

import TableStyle from "styles/application/TableStyle";
import useInfiniteScroll from "hooks/useInfiniteScroll";

import useGroupedTableStyles from "./groupedTableStyles";
import TreeToolbar from "./TreeToolbar";
import GroupedTableRow from "./GroupedTableRow";

const AT = TableStyle();

const GroupedTable = ({
	data,
	childData,
	expandedRows,
	onExpandToggle,
	childLoading,
	tableConfig,
	expandColumnIndex,
	onEdit,
	onDelete,
	onDownloadReport,
	onExpandAll,
	onCollapseAll,
	bulkLoading = false,
	page,
	onPageChange,
	count,
	isLoading,
	searchText,
	isReadOnly = false,
	currentTableSort,
	handleSort,
	customCaptions,
	onViewChange,
	showTreeControls = true,
	isTreeView = true,
	testIdPrefix,
}) => {
	const { classes, cx } = useGroupedTableStyles();
	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [expandState, setExpandState] = useState("collapsed");
	const prevPageRef = useRef(page);

	const treeCol = expandColumnIndex ?? 0;

	const { loading } = useInfiniteScroll(
		data,
		count,
		async (pageNo, prevData, sortOptions, searchText) =>
			await onPageChange(pageNo + 1, prevData, sortOptions),
		page,
		searchText,
		window,
		{
			sortField: currentTableSort[0],
			sortOrder: currentTableSort[1],
		}
	);

	// Reset expand state to neutral when more data loads via infinite scroll
	useEffect(() => {
		if (page > prevPageRef.current) {
			setExpandState("neutral");
		}
		prevPageRef.current = page;
	}, [page]);

	const handleExpandAll = useCallback(() => {
		setExpandState("expanded");
		onExpandAll();
	}, [onExpandAll]);

	const handleCollapseAll = useCallback(() => {
		setExpandState("collapsed");
		onCollapseAll();
	}, [onCollapseAll]);

	const handleExpandToggle = useCallback(
		(rowId) => {
			setExpandState("neutral");
			onExpandToggle(rowId);
		},
		[onExpandToggle]
	);

	const handleSortClick = (field) => {
		const newMethod =
			field === currentTableSort[0] && currentTableSort[1] === "asc"
				? "desc"
				: "asc";
		handleSort(field, newMethod);
	};

	const handlePopup = useCallback((event, key) => {
		setAnchorEl((prev) =>
			prev === event.currentTarget ? null : event.currentTarget
		);
		setSelectedData((prev) => (prev === key ? null : key));
	}, []);

	const handleClosePopup = useCallback(() => {
		setAnchorEl(null);
		setSelectedData(null);
	}, []);

	const getCellValue = useCallback((row, column) => {
		const displayData = row.displayData || row;
		return displayData[column];
	}, []);

	const getMenuData = useCallback(
		(row) => {
			const baseMenu = [
				{
					name: "Edit",
					handler: () => onEdit(row.id),
					isDelete: false,
				},
			];
			if (!isReadOnly) {
				baseMenu.push({
					name: "Delete",
					handler: () => onDelete(row.id),
					isDelete: true,
				});
			}
			baseMenu.push({
				name: `${customCaptions?.taskPlural || "Tasks"} where ${customCaptions?.asset || "Asset"} Used`,
				handler: () => onDownloadReport(row.id),
				isDelete: false,
			});
			return baseMenu;
		},
		[onEdit, onDelete, onDownloadReport, isReadOnly, customCaptions]
	);

	const getHeaderClassName = (column, index) => {
		return cx(classes.nameRow, {
			[classes.selectedTableHeadRow]:
				currentTableSort[0] === tableConfig.columns[index],
			[classes.tableHeadRow]:
				currentTableSort[0] !== tableConfig.columns[index],
		});
	};

	if (isLoading) {
		return <CircularProgress />;
	}

	const menuState = {
		selectedData,
		anchorEl,
		handlePopup,
		handleClosePopup,
	};

	return (
		<AT.TableContainer component={Paper} elevation={0}>
			{showTreeControls && onExpandAll && (
				<TreeToolbar
					onExpandAll={handleExpandAll}
					onCollapseAll={handleCollapseAll}
					expandState={expandState}
					isBulkLoading={bulkLoading}
					disabled={bulkLoading}
					onViewChange={onViewChange}
					testIdPrefix={testIdPrefix ? `${testIdPrefix}-toolbar` : undefined}
				/>
			)}
			<Table aria-label="Grouped Table" data-testid={testIdPrefix ? `${testIdPrefix}-table` : undefined}>
				<AT.TableHead>
					<TableRow>
						{tableConfig.headers.map((header, index) => (
							<TableCell
								key={header}
								onClick={() =>
									handleSortClick(tableConfig.columns[index])
								}
								className={getHeaderClassName(
									tableConfig.columns[index],
									index
								)}
							>
								<AT.CellContainer className="flex justify-between">
									<div
										style={{
											width: "150px",
											paddingLeft:
												isTreeView && index === treeCol
													? 36
													: 0,
										}}
									>
										{header}
									</div>
									<div className="arrow">
										<AT.DescArrow
											fill={
												currentTableSort[0] ===
													tableConfig.columns[
														index
													] &&
												currentTableSort[1] === "asc"
													? "#D2D2D9"
													: "#F9F9FC"
											}
											className="arrowUp"
										/>
										<AT.DefaultArrow
											fill={
												currentTableSort[0] ===
													tableConfig.columns[
														index
													] &&
												currentTableSort[1] === "desc"
													? "#D2D2D9"
													: "#F9F9FC"
											}
											className="arrowDown"
										/>
									</div>
								</AT.CellContainer>
							</TableCell>
						))}
						{!isReadOnly && (
							<TableCell
								className={cx(
									classes.tableHeadRow,
									classes.actionColumn
								)}
							>
								<AT.CellContainer>
									{customCaptions?.action || "Action"}
								</AT.CellContainer>
							</TableCell>
						)}
					</TableRow>
				</AT.TableHead>
				<TableBody>
					{data.map((parentRow) => (
						<GroupedTableRow
							key={parentRow.id}
							row={parentRow}
							depth={0}
							menuKeyPrefix="root"
							tableConfig={tableConfig}
							expandColumnIndex={expandColumnIndex}
							childData={childData}
							childLoading={childLoading}
							expandedRows={expandedRows}
							onExpandToggle={handleExpandToggle}
							isReadOnly={isReadOnly}
							menuState={menuState}
							getCellValue={getCellValue}
							getMenuData={getMenuData}
							isTreeView={showTreeControls && isTreeView}
							testIdPrefix={testIdPrefix}
						/>
					))}
				</TableBody>
			</Table>
			{loading && (
				<div style={{ textAlign: "center", padding: "20px" }}>
					<CircularProgress />
				</div>
			)}
		</AT.TableContainer>
	);
};

GroupedTable.propTypes = {
	data: PropTypes.array.isRequired,
	childData: PropTypes.object.isRequired,
	expandedRows: PropTypes.oneOfType([
		PropTypes.instanceOf(Set),
		PropTypes.object,
	]).isRequired,
	onExpandToggle: PropTypes.func.isRequired,
	childLoading: PropTypes.object.isRequired,
	tableConfig: PropTypes.shape({
		headers: PropTypes.array.isRequired,
		columns: PropTypes.array.isRequired,
	}).isRequired,
	expandColumnIndex: PropTypes.number,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onDownloadReport: PropTypes.func,
	onExpandAll: PropTypes.func,
	onCollapseAll: PropTypes.func,
	bulkLoading: PropTypes.bool,
	page: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	count: PropTypes.number,
	isLoading: PropTypes.bool.isRequired,
	searchText: PropTypes.string,
	isReadOnly: PropTypes.bool,
	currentTableSort: PropTypes.array.isRequired,
	handleSort: PropTypes.func.isRequired,
	customCaptions: PropTypes.object,
	onViewChange: PropTypes.func,
	showTreeControls: PropTypes.bool,
	isTreeView: PropTypes.bool,
	testIdPrefix: PropTypes.string,
};

export default React.memo(GroupedTable);
